import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { CautionText, IconButton, Icons, SkeletonText } from 'utoniq-ui'

import {
  ItemDetailBoxFragment,
  ItemResaleRaffleUserDetailFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ResaleItem } from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getItemSkuPrice } from '@/core/sku'
import { nextRaffleDateTime } from '@/lib/dayjs'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  item: Maybe<ItemDetailBoxFragment>
  itemResaleRaffle: Maybe<ItemResaleRaffleUserDetailFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => void
}

export const ResaleApplicationDetailTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('resaleApplicationDetail')
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleApply'))
  const { routerPages, generatePath, router } = useRouter<{ itemId: string }>()

  return (
    <>
      <BaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistStoreItemDetail, {
            artistId: state.item?.issuedArtist?.id ?? '#',
            itemId: router.query.itemId ?? '#',
          }),
          title: t('title'),
          right: (
            <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
          ),
        }}
      >
        {state.loading ? (
          <ResaleApplicationSkeleton />
        ) : (
          <>
            <Typography variant="h4" sx={{ py: 5 }}>
              {t('resaleInfo', 'リセールチケット情報')}
            </Typography>
            <ResaleAppliedUserCell itemResaleRaffle={state.itemResaleRaffle} />
            <ResaleApplicationDetailContentCell item={state.item} />
            {!state.itemResaleRaffle && (
              <Stack sx={{ pt: 4, pb: 6 }} alignItems="center">
                <Button size="large" sx={{ width: 212 }} onClick={onSubmit}>
                  {t('submit', '応募手続きを行う')}
                </Button>
              </Stack>
            )}
          </>
        )}
      </BaseLayout>
    </>
  )
}

const ResaleApplicationSkeleton: Cell = () => (
  <CellFrame>
    <Stack>
      <Box sx={{ py: 6 }}>
        <SkeletonText variant="h4" width="50%" />
      </Box>
      <Box>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <SkeletonText variant="body2" numberOfLine={2} lastWidth="25%" />
        </Stack>
      </Box>
      <Stack
        spacing={2}
        sx={{
          mt: 4,
          px: 2,
          py: 3,
          borderColor: 'grey.50',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <SkeletonText variant="body2" numberOfLine={2} lastWidth="50%" />
      </Stack>
      {[0, 1].map(index => (
        <Box sx={{ pt: 5, pb: 2 }} key={index}>
          <Stack sx={{ bgcolor: 'grey.50' }}>
            <Skeleton
              variant="rectangular"
              height={20}
              width="100%"
              sx={{ bgcolor: 'grey.100', my: '6px' }}
            />
          </Stack>
          {[0, 1, 2].map(key => (
            <Stack spacing={1} sx={{ pt: 4 }} key={key}>
              <SkeletonText variant="body2" numberOfLine={2} />
            </Stack>
          ))}
        </Box>
      ))}
    </Stack>
    <Stack alignItems="center" sx={{ pt: 8, pb: 6 }}>
      <Skeleton
        variant="rectangular"
        height={44}
        width={212}
        sx={{ borderRadius: 34 }}
      />
    </Stack>
  </CellFrame>
)

type ResaleAppliedUserCellProps = {
  itemResaleRaffle: State['itemResaleRaffle']
}
const ResaleAppliedUserCell: Cell<ResaleAppliedUserCellProps> = ({
  itemResaleRaffle,
}) => {
  const { t } = useTranslation('resaleApplicationDetail')
  const { generatePath, routerPages } = useRouter()

  if (!itemResaleRaffle) {
    return null
  }

  return (
    <CellFrame>
      <CautionText type="error">
        {t('alreadyApplied', 'すでに応募済みです。')}
      </CautionText>
      <Stack sx={{ pt: 5, pb: 7 }} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          href={generatePath(routerPages.ResaleRaffleDetail, {
            itemId: itemResaleRaffle.item.id,
            raffleId: itemResaleRaffle.id,
          })}
        >
          {t('checkResaleApplication', 'リセール応募詳細を確認する')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

type ResaleApplicationDetailContentCellProps = {
  item: State['item']
}
const ResaleApplicationDetailContentCell: Cell<ResaleApplicationDetailContentCellProps> =
  ({ item }) => {
    const { t } = useTranslation('resaleApplicationDetail')
    const { formattedPrice } = useMemo(() => getItemSkuPrice(item), [item])

    const data = [
      {
        label: t('ticketPrice', 'チケット価格'),
        value: t('ticketPriceValue', { price: formattedPrice || 0 }),
      },
      {
        label: t('exhibitNumber', '出品枚数'),
        value: t('exhibitNumberValue', { number: item?.resaleOpenCount || 0 }),
      },
      {
        label: t('applicationNumber', '応募枚数'),
        value: t('applicationNumberValue', {
          number: item?.resaleRaffleAppliedCount || 0,
        }),
      },
    ]

    return (
      <CellFrame>
        <ResaleItem
          title={t('ticketTitle', '本チケットは抽選販売となります')}
          Icon={Icons.Error}
        >
          <Stack spacing={2}>
            <Typography variant="body2">{t('ticketDesc')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('ticketNote')}
            </Typography>
          </Stack>
          <Stack
            spacing={2}
            sx={{
              borderRadius: 2,
              borderColor: 'grey.100',
              borderWidth: 1,
              borderStyle: 'solid',
              py: 4,
              px: 5,
              mb: 5,
            }}
          >
            <Typography variant="body2">
              {t('applicationPeriod', '応募期間', {
                date: `${
                  item?.metadata?.ticket?.resalePeriod?.formattedStartedAt || ''
                }${item ? ' 〜 ' : ''}${
                  item?.metadata?.ticket?.resalePeriod?.formattedEndedAt || ''
                }`,
              })}
            </Typography>
            <Typography variant="body2">
              {t('nextLotteryDate', '次回抽選日時', {
                date: nextRaffleDateTime(),
              })}
            </Typography>
          </Stack>
        </ResaleItem>
        <ResaleItem
          title={t('exhibitionContent', 'チケット情報')}
          Icon={Icons.Post}
        >
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Typography variant="h5">{item?.title}</Typography>
              {!!item?.sku?.unitName && (
                <Typography variant="h5">{item?.sku?.unitName}</Typography>
              )}
            </Stack>

            <Typography variant="body2">
              {item?.metadata?.ticket?.description}
            </Typography>
          </Stack>
        </ResaleItem>
        <ResaleItem title={t('exhibitInfo')}>
          <Stack sx={{ pt: 5 }} spacing={6}>
            {data.map(({ label, value }, index) => (
              <Stack spacing={1} key={index}>
                <Typography variant="subtitle1" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Stack>
            ))}
          </Stack>
        </ResaleItem>
      </CellFrame>
    )
  }
